import type { ObservabilityFailureQueueItem, ObservabilitySignalEvidence, ObservabilitySummaryResponse } from '@scatterin/api-types';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ChartNoAxesColumnIncreasing,
  CircleCheck,
  Clock3,
  MonitorSmartphone,
  RadioTower,
  Server,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { DataPanel } from '@/components/dataPanel';
import { StateView } from '@/components/stateViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { useFailureQueueQuery, useSummaryQuery } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatDuration, formatInteger, formatRelativeTime, formatTime } from '@/lib/format';
import { drillThroughSearch } from '@/lib/navigation';

const responseStaleAfterMs = 5 * 60 * 1000;

const signalContracts: {
  description: string;
  icon: LucideIcon;
  label: string;
  signal: ObservabilitySignalEvidence['signal'];
}[] = [
  { description: 'Workflow and service execution facts', icon: Activity, label: 'Runs', signal: 'runs' },
  { description: 'Operator-visible application and runtime failures', icon: AlertTriangle, label: 'Failures', signal: 'failures' },
  { description: 'Stage and runtime timing facts', icon: ChartNoAxesColumnIncreasing, label: 'Spans', signal: 'spans' },
  { description: 'Explicit emitted metric samples', icon: RadioTower, label: 'Metrics', signal: 'metrics' },
  { description: 'Browser session start facts', icon: MonitorSmartphone, label: 'Browser sessions', signal: 'browser_sessions' },
];

const serviceRanking = (items: ObservabilityFailureQueueItem[] | undefined) => {
  const counts = new Map<string, number>();
  for (const item of items ?? []) {
    const service = item.failure.serviceName || item.failure.source || 'Unattributed';
    counts.set(service, (counts.get(service) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([service, count]) => ({ count, service }))
    .toSorted((left, right) => right.count - left.count || left.service.localeCompare(right.service))
    .slice(0, 5);
};

const latestFailureAt = (items: ObservabilityFailureQueueItem[] | undefined): string | null => {
  const detectedTimes = (items ?? [])
    .map((item) => item.failure.detectedAt)
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value));
  const latest = Math.max(...detectedTimes);
  return Number.isFinite(latest) ? new Date(latest).toISOString() : null;
};

const compactService = (service: string) => (service.length > 18 ? `${service.slice(0, 17)}…` : service);

const SupportingStat = ({ label, value }: { label: string; value: string }) => (
  <div className="border-line bg-raised flex min-h-24 min-w-0 flex-col items-center justify-center rounded-lg border px-4 py-4 text-center">
    <dt className="text-muted text-xs font-semibold tracking-[0.08em] uppercase">{label}</dt>
    <dd className="numeric text-ink mt-2 text-xl font-semibold text-balance">{value}</dd>
  </div>
);

const CoverageStatus = ({ evidence }: { evidence?: ObservabilitySignalEvidence }) => {
  if (!evidence) {
    return <Badge tone="amber">Evidence unavailable</Badge>;
  }
  if ((evidence.observations ?? 0) === 0) {
    return <Badge tone="slate">No evidence in window</Badge>;
  }
  return <Badge tone="green">Observed</Badge>;
};

const DurationEvidence = ({
  count,
  p50,
  p95,
  p99,
  unit,
}: {
  count: number;
  p50?: number | null;
  p95?: number | null;
  p99?: number | null;
  unit: string;
}) => {
  if (count === 0) {
    return (
      <StateView title="No run-duration samples in this window" tone="coverage">
        A zero sample count is collection evidence, not a healthy performance result. Open Performance to inspect a different workflow or
        time range.
      </StateView>
    );
  }

  const percentileCards = [
    { description: 'Half of recorded runs completed within', label: 'P50', title: 'Typical run', value: p50 },
    { description: '95% of recorded runs completed within', label: 'P95', title: 'Slow tail', value: p95 },
    { description: '99% of recorded runs completed within', label: 'P99', title: 'Extreme tail', value: p99 },
  ];
  const p95ToP50 = typeof p50 === 'number' && p50 > 0 && typeof p95 === 'number' ? p95 / p50 : null;

  return (
    <div className="grid gap-4" role="group" aria-label={`${count} run-duration samples`}>
      <p className="text-muted text-sm leading-6">
        Queued-to-terminal duration for runs with both timing boundaries recorded in this window.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {percentileCards.map(({ description, label, title, value }) => (
          <div className="border-line bg-raised rounded-lg border p-4 text-center" key={label}>
            <div className="flex items-center justify-center gap-2">
              <p className="text-muted text-xs font-semibold tracking-[0.08em] uppercase">{label}</p>
              <span className="text-muted text-xs">{title}</span>
            </div>
            <p className="numeric text-ink mt-3 text-2xl font-semibold">{formatDuration(value, unit)}</p>
            <p className="text-muted mt-2 text-xs leading-5">{description}</p>
          </div>
        ))}
      </div>
      <div className="border-line flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <p className="text-muted text-xs">
          Based on <span className="numeric text-ink font-semibold">{formatInteger(count)}</span> recorded durations
        </p>
        {p95ToP50 ? (
          <p className="text-muted text-xs">
            Tail separation <span className="numeric text-ink font-semibold">P95 is {p95ToP50.toFixed(1)}× P50</span>
          </p>
        ) : null}
      </div>
    </div>
  );
};

const AffectedServicesLoading = () => (
  <div aria-label="Loading affected-service ranking" className="grid gap-4" role="status">
    <span className="sr-only">Loading affected services</span>
    <div className="flex h-52 flex-col justify-center gap-5">
      {[82, 64, 46].map((width, index) => (
        <div className="grid grid-cols-[6.5rem_1fr] items-center gap-3" key={width}>
          <Skeleton className="h-3" style={{ width: `${72 - index * 10}%` }} />
          <Skeleton className="h-7" style={{ width: `${width}%` }} />
        </div>
      ))}
    </div>
    <div className="border-line grid gap-3 border-t pt-3">
      {[0, 1, 2].map((item) => (
        <div className="flex items-center justify-between gap-3" key={item}>
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-3 w-6" />
        </div>
      ))}
    </div>
  </div>
);

type Situation = {
  actionLabel: string;
  actionPath: string;
  actionSearch: string;
  description: string;
  title: string;
};

const currentSituation = ({
  affectedServices,
  failuresSearch,
  latestFailure,
  observedSignals,
  summary,
  systemSearch,
}: {
  affectedServices: { count: number; service: string }[];
  failuresSearch: string;
  latestFailure: string | null;
  observedSignals: number;
  summary: ObservabilitySummaryResponse;
  systemSearch: string;
}): Situation => {
  if ((summary.failures.openFailures ?? 0) > 0) {
    let affectedCopy = 'Affected-service evidence is not available yet';
    if (affectedServices.length === 1) {
      affectedCopy = '1 affected service appears in the visible open queue';
    } else if (affectedServices.length > 1) {
      affectedCopy = `${affectedServices.length} affected services appear in the visible open queue`;
    }
    const latestCopy = latestFailure ? `; the latest occurrence was ${formatRelativeTime(latestFailure)}.` : '.';
    return {
      actionLabel: 'Review failure queue',
      actionPath: '/failures',
      actionSearch: failuresSearch,
      description: `${formatInteger(summary.failures.unconsumedFailures)} are unreviewed. ${affectedCopy}${latestCopy}`,
      title: `${formatInteger(summary.failures.openFailures)} unresolved failures need review`,
    };
  }

  if (observedSignals === signalContracts.length) {
    return {
      actionLabel: 'Inspect collection evidence',
      actionPath: '/system',
      actionSearch: systemSearch,
      description: 'All five governed signal families contain evidence. Zero unresolved failures is supported by the selected data.',
      title: 'No unresolved failures are recorded in this window',
    };
  }

  return {
    actionLabel: 'Inspect collection evidence',
    actionPath: '/system',
    actionSearch: systemSearch,
    description:
      observedSignals > 0
        ? 'The absence of failures is not presented as healthy because one or more governed signal families have no evidence.'
        : 'No governed signal family contains evidence, so zero activity is not presented as healthy.',
    title: observedSignals > 0 ? 'No unresolved failures are recorded in this window' : 'No telemetry evidence is recorded in this window',
  };
};

const coveragePresentation = ({
  evidenceComplete,
  observedSignals,
  responseIsStale,
}: {
  evidenceComplete: boolean;
  observedSignals: number;
  responseIsStale: boolean;
}): { label: string; tone: 'amber' | 'blue' | 'green' } => {
  if (responseIsStale) {
    return { label: 'Response older than 5 min', tone: 'amber' };
  }
  if (!evidenceComplete) {
    return { label: 'Coverage metadata missing', tone: 'amber' };
  }
  if (observedSignals === signalContracts.length) {
    return { label: 'All 5 signals observed', tone: 'green' };
  }
  return { label: `${observedSignals} of ${signalContracts.length} observed`, tone: 'blue' };
};

const AffectedServicesPanel = ({
  affectedServices,
  isError,
  isLoading,
}: {
  affectedServices: { count: number; service: string }[];
  isError: boolean;
  isLoading: boolean;
}) => {
  if (isError) {
    return (
      <StateView title="Affected-service evidence failed to load" tone="error">
        The situation count remains available, but service ranking cannot be trusted until the queue request succeeds.
      </StateView>
    );
  }

  if (isLoading) {
    return <AffectedServicesLoading />;
  }

  if (affectedServices.length === 0) {
    return (
      <StateView title="No affected services in the visible queue" tone="neutral">
        There are no service-attributed open failure rows for this scope.
      </StateView>
    );
  }

  const summary = affectedServices.map(({ count, service }) => `${service}: ${count}`).join('; ');

  return (
    <figure>
      <div aria-label={`Affected services ranked by visible open failures. ${summary}`} className="h-52" role="img">
        <ResponsiveContainer height="100%" width="100%">
          <BarChart data={affectedServices} layout="vertical" margin={{ bottom: 0, left: 0, right: 12, top: 0 }}>
            <XAxis allowDecimals={false} axisLine={false} tick={{ fontSize: 11 }} tickLine={false} type="number" />
            <YAxis
              axisLine={false}
              dataKey="service"
              tick={{ fontSize: 11 }}
              tickFormatter={compactService}
              tickLine={false}
              type="category"
              width={118}
            />
            <Tooltip />
            <Bar dataKey="count" fill="var(--color-critical)" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <figcaption className="sr-only">{summary}</figcaption>
      <ol className="border-line mt-3 grid gap-2 border-t pt-3 text-sm" aria-label="Affected-service data">
        {affectedServices.map(({ count, service }) => (
          <li className="flex min-w-0 items-center justify-between gap-3" key={service}>
            <span className="text-ink min-w-0 truncate">{service}</span>
            <span className="numeric text-muted shrink-0">{formatInteger(count)}</span>
          </li>
        ))}
      </ol>
    </figure>
  );
};

export const OverviewPage = () => {
  const { state } = useRouteContext();
  const summaryQuery = useSummaryQuery(state);
  const failureQueueQuery = useFailureQueueQuery(state);
  const location = useLocation();

  const summary = summaryQuery.data;
  const canonicalScope = {
    analysisTypes: state.analysisTypes,
    end: state.end.toISOString(),
    env: state.env,
    runtimeTypes: state.runtimeTypes,
    serviceNames: state.serviceNames,
    sources: state.sources,
    start: state.start.toISOString(),
  };
  const failuresSearch = drillThroughSearch(location.search, { ...canonicalScope, queueView: 'open' });
  const performanceSearch = drillThroughSearch(location.search, { ...canonicalScope, view: 'runs' });
  const frontendSearch = drillThroughSearch(location.search, canonicalScope);
  const usageSearch = drillThroughSearch(location.search, canonicalScope);
  const systemSearch = drillThroughSearch(location.search, { ...canonicalScope, view: 'collection' });
  const affectedServices = serviceRanking(failureQueueQuery.data?.items);
  const latestFailure = latestFailureAt(failureQueueQuery.data?.items);
  const evidenceBySignal = new Map((summary?.signalEvidence ?? []).map((evidence) => [evidence.signal, evidence]));
  const observedSignals = [...evidenceBySignal.values()].filter((evidence) => (evidence.observations ?? 0) > 0).length;
  const evidenceComplete = signalContracts.every((contract) => evidenceBySignal.has(contract.signal));
  const generatedAt = summary?.generatedAt ? new Date(summary.generatedAt) : null;
  const responseIsStale = generatedAt ? summaryQuery.dataUpdatedAt - generatedAt.getTime() > responseStaleAfterMs : false;
  const hasOpenFailures = (summary?.failures.openFailures ?? 0) > 0;
  const coverage = coveragePresentation({ evidenceComplete, observedSignals, responseIsStale });
  const situation = summary
    ? currentSituation({ affectedServices, failuresSearch, latestFailure, observedSignals, summary, systemSearch })
    : null;

  return (
    <DataPanel
      error={summaryQuery.error}
      isError={summaryQuery.isError}
      isLoading={summaryQuery.isLoading}
      loadingLabel="Loading current situation and collection evidence"
      onRetry={() => void summaryQuery.refetch()}
    >
      {summary ? (
        <div className="grid gap-5">
          <Card className={hasOpenFailures ? 'border-critical/25 bg-critical-wash' : 'border-info/25 bg-info-wash'}>
            <CardContent className="grid gap-5 p-5 md:grid-cols-[1fr_auto] md:items-end md:p-6">
              <div className="max-w-3xl">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge tone={hasOpenFailures ? 'red' : coverage.tone}>{hasOpenFailures ? 'Action required' : 'Current situation'}</Badge>
                  <span className="text-muted text-xs">
                    {generatedAt ? `Summary generated ${formatRelativeTime(summary.generatedAt)}` : 'Summary freshness unavailable'}
                  </span>
                </div>
                <h2 className="text-ink text-2xl font-semibold tracking-[-0.03em] text-balance md:text-3xl">{situation?.title}</h2>
                <p className="text-muted mt-3 max-w-2xl text-sm leading-6">{situation?.description}</p>
              </div>
              <Button asChild variant="primary">
                <Link to={{ pathname: situation?.actionPath, search: situation?.actionSearch }}>
                  {situation?.actionLabel}
                  <ArrowRight aria-hidden="true" className="size-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <SupportingStat label="Runs" value={formatInteger(summary.runs.totalRuns)} />
            <SupportingStat label="Failed runs" value={formatInteger(summary.runs.failedRuns)} />
            <SupportingStat label="Browser sessions" value={formatInteger(summary.rum.sessions)} />
            <SupportingStat
              label="Active principals"
              value={`${formatInteger(summary.usage.uniqueUsers)} users · ${formatInteger(summary.usage.uniqueProjects)} projects`}
            />
          </dl>

          <section className="grid gap-5 xl:grid-cols-[0.7fr_1.3fr]">
            <Card>
              <CardHeader className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Server aria-hidden="true" className="text-muted size-4" />
                  <CardTitle>Affected services</CardTitle>
                </div>
                <span className="text-muted text-xs">Visible open queue</span>
              </CardHeader>
              <CardContent>
                <AffectedServicesPanel
                  affectedServices={affectedServices}
                  isError={failureQueueQuery.isError}
                  isLoading={failureQueueQuery.isLoading}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <RadioTower aria-hidden="true" className="text-muted size-4" />
                  <CardTitle>Visibility coverage</CardTitle>
                </div>
                <Badge tone={coverage.tone}>{coverage.label}</Badge>
              </CardHeader>
              <CardContent className="grid gap-3">
                <p className="text-muted text-sm leading-6">
                  Coverage means recorded evidence inside the selected window. It does not assert instrumentation completeness or service
                  health.
                </p>
                <TableShell>
                  <Table aria-label="Governed signal evidence" className="min-w-[44rem] xl:table-fixed">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="xl:w-[40%]">Signal</TableHead>
                        <TableHead className="xl:w-[18%]">Status</TableHead>
                        <TableHead className="text-right xl:w-[12%]">Evidence</TableHead>
                        <TableHead className="xl:w-[15%]">Last event</TableHead>
                        <TableHead className="xl:w-[15%]">Last received</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {signalContracts.map((contract) => {
                        const evidence = evidenceBySignal.get(contract.signal);
                        const Icon = contract.icon;
                        return (
                          <TableRow key={contract.signal}>
                            <TableCell>
                              <div className="flex items-start gap-2">
                                <Icon aria-hidden="true" className="text-muted mt-0.5 size-4 shrink-0" />
                                <div>
                                  <p className="font-medium">{contract.label}</p>
                                  <p className="text-muted mt-0.5 text-xs">{contract.description}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <CoverageStatus evidence={evidence} />
                            </TableCell>
                            <TableCell className="numeric text-right">{evidence ? formatInteger(evidence.observations) : '—'}</TableCell>
                            <TableCell
                              className="text-muted"
                              title={evidence?.lastObservedAt ? formatTime(evidence.lastObservedAt) : undefined}
                            >
                              {evidence?.lastObservedAt ? formatRelativeTime(evidence.lastObservedAt) : '—'}
                            </TableCell>
                            <TableCell
                              className="text-muted"
                              title={evidence?.lastReceivedAt ? formatTime(evidence.lastReceivedAt) : undefined}
                            >
                              {evidence?.lastReceivedAt ? formatRelativeTime(evidence.lastReceivedAt) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableShell>
              </CardContent>
            </Card>
          </section>

          <section className="grid gap-5 xl:grid-cols-[1fr_0.72fr]">
            <Card>
              <CardHeader className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Clock3 aria-hidden="true" className="text-muted size-4" />
                  <CardTitle>Run-duration collection</CardTitle>
                </div>
                <Badge tone="slate">{formatInteger(summary.runs.durations.runDuration.count)} samples</Badge>
              </CardHeader>
              <CardContent>
                <DurationEvidence {...summary.runs.durations.runDuration} count={summary.runs.durations.runDuration.count ?? 0} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex items-center gap-2">
                <CircleCheck aria-hidden="true" className="text-muted size-4" />
                <CardTitle>Continue the investigation</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2">
                {[
                  ['Performance', '/performance', performanceSearch],
                  ['Frontend experience', '/frontend-experience', frontendSearch],
                  ['Usage', '/usage', usageSearch],
                  ['System coverage', '/system', systemSearch],
                ].map(([label, pathname, search]) => (
                  <Button asChild className="justify-between" key={pathname} variant="ghost">
                    <Link to={{ pathname, search }}>
                      {label}
                      <ArrowRight aria-hidden="true" className="size-4" />
                    </Link>
                  </Button>
                ))}
              </CardContent>
            </Card>
          </section>
        </div>
      ) : null}
    </DataPanel>
  );
};
